export type BankName =
  | "swedbank"
  | "seb"
  | "nordea"
  | "handelsbanken"
  | "lansforsakringar"
  | "ica-banken"
  | "avanza"
  | "unknown";

export const DEFAULT_TOTALS_BANKS: BankName[] = [
  "swedbank",
  "seb",
  "nordea",
  "handelsbanken",
  "lansforsakringar",
];

export type MainCategory =
  | "food_drink"
  | "housing"
  | "transport"
  | "shopping"
  | "entertainment"
  | "health"
  | "subscriptions"
  | "income"
  | "savings"
  | "transfer"
  | "other";

export type TransactionType =
  | "expense"
  | "income"
  | "transfer"
  | "fee"
  | "refund"
  | "payment"
  | "card"
  | "other";

export const MAIN_CATEGORIES: MainCategory[] = [
  "food_drink",
  "housing",
  "transport",
  "shopping",
  "entertainment",
  "health",
  "subscriptions",
  "income",
  "savings",
  "transfer",
  "other",
];

/** Subcategories grouped by main category */
export const SUBCATEGORIES: Record<MainCategory, string[]> = {
  food_drink: ["groceries", "restaurants", "cafes", "fast_food", "delivery", "alcohol"],
  housing: ["rent", "mortgage", "electricity", "internet", "insurance", "maintenance", "furniture"],
  transport: ["public_transit", "fuel", "car", "taxi", "parking", "bike"],
  shopping: ["clothes", "electronics", "home", "online", "gifts"],
  entertainment: ["cinema", "games", "concerts", "sports", "streaming", "hobbies"],
  health: ["pharmacy", "doctor", "dentist", "therapy", "gym", "wellness"],
  subscriptions: ["digital", "news", "software", "membership"],
  income: ["salary", "freelance", "refund", "benefits", "interest"],
  savings: ["savings_account", "investments", "pension"],
  transfer: ["internal", "external"],
  other: ["uncategorized", "fees", "cash", "misc"],
};

/** Flat list of all subcategory values */
export function allSubcategories(): string[] {
  return Object.values(SUBCATEGORIES).flat();
}

// Keep backward compat type alias
export type Category = MainCategory;

export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  balance?: number;
  category: MainCategory;
  subcategory: string;
  bank: BankName;
  accountNumber?: string;
  accountName?: string;
  transactionType?: TransactionType;
  importMethod?: "bank_parser" | "generic_parser" | "ai";
  confidence?: number;
  isTransfer: boolean;
  uploadBatchId: string;
  sourceId?: string;
  sourceLabel?: string;
  includedInTotals?: boolean;
}

export interface ExpenseSource {
  id: string;
  label: string;
  bank: BankName;
  transactionCount: number;
  expenseCount: number;
  totalExpenses: number;
  defaultIncluded: boolean;
}

/**
 * A transaction counts as a transfer if the detector flagged it OR if it is
 * categorized as one (AI/keyword categorization and manual edits can set the
 * category without the flag). All stats must exclude both.
 */
export function isTransferTransaction(
  tx: Pick<Transaction, "isTransfer" | "category">
): boolean {
  return tx.isTransfer || tx.category === "transfer";
}

function normalizeSourcePart(value: string | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function getTransactionSourceId(
  tx: Pick<Transaction, "bank" | "accountName" | "accountNumber">
): string {
  const account = normalizeSourcePart(tx.accountName || tx.accountNumber);
  return account ? `${tx.bank}:${account}` : tx.bank;
}

export function getTransactionSourceLabel(
  tx: Pick<Transaction, "bank" | "accountName" | "accountNumber">
): string {
  const parts = [tx.bank, tx.accountName, tx.accountNumber]
    .map((part) => part?.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts.join(" · ") : tx.bank;
}

export function isDefaultIncludedBank(bank: BankName): boolean {
  return DEFAULT_TOTALS_BANKS.includes(bank);
}

export function isExpenseIncludedInTotals(
  tx: Pick<Transaction, "amount" | "bank" | "accountName" | "accountNumber" | "isTransfer" | "category">,
  includedSourceIds?: string[] | null
): boolean {
  if (tx.amount >= 0 || isTransferTransaction(tx)) return false;
  if (!includedSourceIds) return isDefaultIncludedBank(tx.bank);
  return includedSourceIds.includes(getTransactionSourceId(tx));
}

/** Monthly budget: SEK amounts per main category plus an income target */
export interface Budget {
  incomeTarget: number | null;
  categories: Record<string, number>;
}

export interface UploadBatch {
  id: string;
  fileName: string;
  bank: BankName;
  uploadedAt: string;
  transactionCount: number;
}

export interface SpendingSummary {
  totalSpending: number;
  totalIncome: number;
  byCategory: Partial<Record<MainCategory, number>>;
  bySubcategory: Partial<Record<string, number>>;
  byMonth: Array<{
    month: string;
    spending: number;
    income: number;
  }>;
  transactionCount: number;
  includedExpenseCount: number;
  shownOnlyExpenseCount: number;
  transferCount: number;
  includedSourceIds: string[];
}

export interface UploadResponse {
  batch: UploadBatch;
  transactions: Transaction[];
  detectedBank: BankName;
  transfersDetected: number;
}

// ---------------------------------------------------------------------------
// Accounts (assets & liabilities, incl. credit cards and loans)
// ---------------------------------------------------------------------------

export type AccountKind =
  | "bank"
  | "savings"
  | "cash"
  | "property"
  | "vehicle"
  | "other_asset"
  | "credit_card"
  | "loan"
  | "mortgage"
  | "other_liability";

export const ASSET_KINDS: AccountKind[] = [
  "bank",
  "savings",
  "cash",
  "property",
  "vehicle",
  "other_asset",
];

export const LIABILITY_KINDS: AccountKind[] = [
  "credit_card",
  "loan",
  "mortgage",
  "other_liability",
];

export function isLiabilityKind(kind: AccountKind): boolean {
  return LIABILITY_KINDS.includes(kind);
}

export interface BalancePoint {
  date: string; // YYYY-MM-DD
  balance: number;
}

/** A manually tracked account. `balance` is always positive — for liabilities it is the debt amount. */
export interface ManualAccount {
  id: string;
  name: string;
  kind: AccountKind;
  owner?: string;
  institution?: string;
  balance: number;
  /** Credit cards: the card limit, for utilization */
  creditLimit?: number;
  /** Loans/mortgages: yearly interest rate in percent */
  interestRate?: number;
  note?: string;
  updatedAt: string;
  history: BalancePoint[];
}

/** A bank balance derived from imported statements (latest known balance per source). */
export interface DerivedBankAccount {
  sourceId: string;
  label: string;
  bank: BankName;
  balance: number;
  balanceDate: string;
  includedInNetWorth: boolean;
}

// ---------------------------------------------------------------------------
// Investments
// ---------------------------------------------------------------------------

export type InvestmentAssetType =
  | "stock"
  | "fund"
  | "etf"
  | "certificate"
  | "bond"
  | "other";

export interface InvestmentHolding {
  id: string;
  name: string;
  symbol?: string;
  isin?: string;
  assetType: InvestmentAssetType;
  /** Depå/ISK/KF label, e.g. "Avanza ISK" */
  account?: string;
  quantity: number;
  /** Price per unit in SEK (from import or manual edit) */
  price?: number;
  /** Market value in SEK */
  value: number;
  /** Total cost basis in SEK (GAV × quantity) */
  costBasis?: number;
  updatedAt: string;
}

export interface PortfolioSnapshot {
  date: string;
  value: number;
  costBasis?: number;
}

export interface InvestmentsOverview {
  holdings: InvestmentHolding[];
  totalValue: number;
  totalCostBasis: number;
  history: PortfolioSnapshot[];
  accounts: string[];
}

export interface PositionsImportResponse {
  imported: number;
  accounts: string[];
  holdings: InvestmentHolding[];
  importMethod: "parser" | "ai";
}

// ---------------------------------------------------------------------------
// Crypto
// ---------------------------------------------------------------------------

export interface CryptoHolding {
  id: string;
  /** CoinGecko coin id, e.g. "bitcoin" */
  coinId: string;
  symbol: string;
  name: string;
  quantity: number;
  costBasis?: number;
  /** Fallback when live prices are unavailable */
  manualPriceSek?: number;
  updatedAt: string;
}

export interface CryptoHoldingWithPrice extends CryptoHolding {
  priceSek: number | null;
  value: number;
  change24hPct?: number;
  priceSource: "live" | "cached" | "manual" | "none";
}

export interface CryptoOverview {
  holdings: CryptoHoldingWithPrice[];
  totalValue: number;
  totalCostBasis: number;
  pricesFetchedAt: string | null;
}

// ---------------------------------------------------------------------------
// Recurring costs
// ---------------------------------------------------------------------------

export type RecurringCadence = "weekly" | "monthly" | "quarterly" | "yearly";

export interface RecurringItem {
  /** Detection key (normalized description) or "manual:<id>" */
  key: string;
  name: string;
  cadence: RecurringCadence;
  /** Typical charge amount, positive SEK */
  amount: number;
  /** Amount normalized to per month */
  monthlyAmount: number;
  category: MainCategory;
  subcategory: string;
  lastDate?: string;
  nextDate?: string;
  occurrences?: number;
  source: "detected" | "manual";
  /** False when charges stopped appearing */
  active: boolean;
  ignored: boolean;
}

export interface RecurringOverview {
  items: RecurringItem[];
  activeMonthlyTotal: number;
  activeCount: number;
}

// ---------------------------------------------------------------------------
// Net worth
// ---------------------------------------------------------------------------

export interface NetWorthBreakdown {
  bankAccounts: number;
  manualAssets: number;
  investments: number;
  crypto: number;
  creditCards: number;
  loans: number;
  otherLiabilities: number;
}

export interface NetWorthSnapshot {
  date: string;
  assets: number;
  liabilities: number;
  net: number;
  breakdown: NetWorthBreakdown;
}

export interface NetWorthOverview {
  current: NetWorthSnapshot;
  history: NetWorthSnapshot[];
}

// ---------------------------------------------------------------------------
// AI assistant
// ---------------------------------------------------------------------------

export interface AssistantMessage {
  role: "user" | "assistant";
  content: string;
}

// ---------------------------------------------------------------------------
// Transaction list filtering
// ---------------------------------------------------------------------------

/**
 * Type filter for the transactions page. Each summary card deep-links to the
 * set of transactions its number is computed from.
 */
export type TransactionTypeFilter =
  | "all"        // everything except transfers
  | "counted"    // included in the overview: income + expenses that count
  | "expenses"   // expenses included in totals
  | "income"     // all income (transfers excluded)
  | "shown_only" // expenses from excluded sources — shown but never counted
  | "transfers"; // transfers only

export function matchesTypeFilter(
  tx: Pick<Transaction, "amount" | "isTransfer" | "category" | "includedInTotals">,
  filter: TransactionTypeFilter
): boolean {
  const isTransfer = isTransferTransaction(tx);
  switch (filter) {
    case "all":
      return !isTransfer;
    case "counted":
      return !isTransfer && (tx.amount >= 0 || tx.includedInTotals === true);
    case "expenses":
      return !isTransfer && tx.amount < 0 && tx.includedInTotals === true;
    case "income":
      return !isTransfer && tx.amount > 0;
    case "shown_only":
      return !isTransfer && tx.amount < 0 && tx.includedInTotals !== true;
    case "transfers":
      return isTransfer;
  }
}
